import { Component } from '@theme/component';

/**
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources
 * @property {HTMLVideoElement} videoElement
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  connectedCallback() {
    super.connectedCallback();

    if (this.videoLoaded) return;

    if (!('IntersectionObserver' in window)) {
      this.loadVideo();
      return;
    }

    this.videoObserver?.disconnect();

    this.videoObserver = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.videoObserver.disconnect();
          this.loadVideo();
        }
      },
      {
        rootMargin: '300px 0px',
        threshold: 0,
      }
    );

    this.videoObserver.observe(this);
  }

  loadVideo() {
    if (this.videoLoaded) return;

    const { videoSources, videoElement } = this.refs;

    for (const source of videoSources) {
      const { videoSource } = source.dataset;

      if (videoSource) {
        source.setAttribute('src', videoSource);
      }
    }

    this.videoLoaded = true;
    videoElement.load();

    if (videoElement.autoplay) {
      videoElement.play().catch(() => {
        // The browser may restrict autoplay.
      });
    }
  }

  disconnectedCallback() {
    this.videoObserver?.disconnect();
    super.disconnectedCallback();
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define(
    'video-background-component',
    VideoBackgroundComponent
  );
}